import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check } from "lucide-react";

import { ADDONS, PLANS } from "../../../util/admin/features";
import { AnimatedNumber, EASE, Em, MaskLines, PillButton, Reveal, SectionTag, euro } from "./primitives";

const BASE_FEATURES = [
   "Eigen webshop op je eigen link",
   "Menu-, product- en optiebeheer",
   "Afhaalmomenten & levering per postcode",
   "Kortingen & collecties",
   "Online betalen met Bancontact & kaart",
   "Dashboard & pushmeldingen",
];
const PRO_FEATURES = ["Online reservaties", "Tafel- en capaciteitsbeheer", "Sluitingsdagen & speciale uren"];

const PLAN_INFO = {
   basic: { tagline: "Alles om online te verkopen" },
   pro: { tagline: "Basis + online reservaties", tag: "Meest gekozen" },
};

function Switch({ on, onChange, label }) {
   return (
      <button
         role="switch"
         aria-checked={on}
         aria-label={label}
         onClick={() => onChange(!on)}
         className={`relative h-8 w-14 flex-shrink-0 rounded-full transition-colors duration-300 ${on ? "bg-lp-blue" : "bg-lp-ink/15"}`}
      >
         <motion.span
            layout
            transition={{ type: "spring", stiffness: 500, damping: 32 }}
            className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow ${on ? "right-1" : "left-1"}`}
         />
      </button>
   );
}

/* Regel op de rekening */
const BillLine = ({ qty, label, amount, muted }) => (
   <motion.li
      layout
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.45, ease: EASE }}
      className={`overflow-hidden ${muted ? "text-lp-muted" : ""}`}
   >
      <div className="flex items-baseline py-1">
         <span className="w-8">{qty}×</span>
         <span>{label}</span>
         <span className="lp-leader" />
         <span className="tabular-nums">{euro(amount)}</span>
      </div>
   </motion.li>
);

export default function PricingBill() {
   const [plan, setPlan] = useState("pro");
   const [gifts, setGifts] = useState(false);
   const total = PLANS[plan].price + (gifts ? ADDONS.giftCards.price : 0);
   const features = [
      ...BASE_FEATURES.map((f) => ({ f, extra: false })),
      ...(plan === "pro" ? PRO_FEATURES.map((f) => ({ f, extra: true })) : []),
      ...(gifts ? [{ f: "Digitale cadeaubonnen", extra: true }] : []),
   ];
   const today = new Date().toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" });

   return (
      <section id="prijzen" className="lp-grain relative bg-lp-paper px-5 py-28 sm:px-8 sm:py-40">
         <div className="mx-auto max-w-[1400px]">
            <SectionTag>Prijzen</SectionTag>
            <MaskLines
               as="h2"
               className="mt-8 text-[clamp(2.8rem,6.4vw,6rem)] font-semibold leading-[0.92] tracking-[-0.05em] text-lp-ink"
               lines={["Eerlijke prijs. ", <Em className="text-lp-blue">Nul commissie.</Em>]}
            />

            <div className="mt-16 grid gap-14 lg:grid-cols-12 lg:gap-10">
               {/* keuze */}
               <div className="lg:col-span-6">
                  <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Plan">
                     {["basic", "pro"].map((id) => {
                        const on = plan === id;
                        return (
                           <button
                              key={id}
                              role="radio"
                              aria-checked={on}
                              onClick={() => setPlan(id)}
                              className={`relative overflow-hidden rounded-3xl border p-6 text-left transition-colors duration-500 ${
                                 on ? "border-lp-ink text-lp-paper" : "border-lp-ink/10 bg-lp-card text-lp-ink hover:border-lp-ink/30"
                              }`}
                           >
                              {on && <motion.span layoutId="plan-bg" className="absolute inset-0 bg-lp-ink" transition={{ duration: 0.55, ease: EASE }} />}
                              <span className="relative flex items-center justify-between">
                                 <span className="text-lg font-medium">{PLANS[id].name}</span>
                                 {PLAN_INFO[id].tag && (
                                    <span className="rounded-full bg-lp-blue px-2.5 py-1 font-ticket text-[9px] font-bold uppercase tracking-[0.14em] text-white">
                                       {PLAN_INFO[id].tag}
                                    </span>
                                 )}
                              </span>
                              <span className="relative mt-6 flex items-baseline gap-1">
                                 <span className="text-5xl font-semibold tracking-[-0.05em]">€{PLANS[id].price}</span>
                                 <span className={on ? "text-lp-paper/60" : "text-lp-muted"}>/maand</span>
                              </span>
                              <span className={`relative mt-2 block text-sm ${on ? "text-lp-paper/60" : "text-lp-muted"}`}>{PLAN_INFO[id].tagline}</span>
                           </button>
                        );
                     })}
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-4 rounded-3xl border border-lp-ink/10 bg-lp-card p-6">
                     <div>
                        <p className="text-lg font-medium text-lp-ink">
                           {ADDONS.giftCards.name} <span className="text-lp-muted">+€{ADDONS.giftCards.price}/maand</span>
                        </p>
                        <p className="mt-1 text-sm text-lp-muted">Add-on, te combineren met elk plan.</p>
                     </div>
                     <Switch on={gifts} onChange={setGifts} label="Cadeaubonnen toevoegen" />
                  </div>

                  <ul className="mt-10 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                     <AnimatePresence initial={false}>
                        {features.map(({ f, extra }) => (
                           <motion.li
                              key={f}
                              layout
                              initial={{ opacity: 0, x: -12 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -12 }}
                              transition={{ duration: 0.4, ease: EASE }}
                              className="flex items-start gap-3 text-[15px] text-lp-ink"
                           >
                              <span
                                 className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${
                                    extra ? "bg-lp-blue text-white" : "bg-lp-ink/[0.07] text-lp-ink"
                                 }`}
                              >
                                 <Check size={12} strokeWidth={3} />
                              </span>
                              {f}
                           </motion.li>
                        ))}
                     </AnimatePresence>
                  </ul>
               </div>

               {/* de rekening */}
               <Reveal y={60} className="lg:col-span-5 lg:col-start-8">
                  <div className="mx-auto max-w-[420px] [filter:drop-shadow(0_40px_40px_rgba(18,17,16,0.16))]">
                     <div className="lp-ticket rotate-[-1.5deg] px-7 pb-10 pt-10 font-ticket text-[13px] text-lp-ink">
                        <p className="text-center text-base font-bold tracking-[0.22em]">RESTO-ONLINE</p>
                        <p className="mt-1 text-center text-[11px] text-lp-muted">resto-online.be</p>
                        <div className="mt-5 flex justify-between text-[11px] text-lp-muted">
                           <span>Datum {today}</span>
                           <span>Tafel 1 · jouw zaak</span>
                        </div>
                        <div className="my-4 border-t border-dashed border-lp-ink/30" />
                        <ul>
                           <AnimatePresence initial={false}>
                              <BillLine key={`plan-${plan}`} qty={1} label={`Plan ${PLANS[plan].name}`} amount={PLANS[plan].price} />
                              {gifts && <BillLine key="gifts" qty={1} label="Cadeaubonnen" amount={ADDONS.giftCards.price} />}
                              <BillLine key="comm" qty={0} label="Commissie" amount={0} muted />
                              <BillLine key="setup" qty={0} label="Opstartkost" amount={0} muted />
                           </AnimatePresence>
                        </ul>
                        <div className="my-4 border-t border-dashed border-lp-ink/30" />
                        <div className="flex items-baseline justify-between">
                           <span className="font-bold uppercase tracking-[0.14em]">Totaal / maand</span>
                           <span className="text-3xl font-bold tracking-[-0.03em]">
                              <AnimatedNumber value={total} format={euro} />
                           </span>
                        </div>
                        <div className="my-4 border-t border-dashed border-lp-ink/30" />
                        <p className="text-center text-[11px] leading-relaxed text-lp-muted">
                           Maandelijks via Stripe. Je gasten betalen
                           <br />
                           rechtstreeks aan jou.
                        </p>
                        <div className="mx-auto mt-5 flex h-10 w-fit items-stretch gap-[2px]">
                           {"21131221413121143112213121".split("").map((w, i) => (
                              <span key={i} className={i % 2 ? "" : "bg-lp-ink"} style={{ width: `${Number(w) * 2}px` }} />
                           ))}
                        </div>
                        <p className="mt-4 text-center text-[11px] tracking-[0.2em]">*** BEDANKT & SMAKELIJK ***</p>
                     </div>
                  </div>
                  <div className="mt-10 flex justify-center">
                     <PillButton href="/registreren" variant="blue" size="lg">
                        Start met {PLANS[plan].name}
                     </PillButton>
                  </div>
               </Reveal>
            </div>
         </div>
      </section>
   );
}
